import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { states } from "../../../utils/data";

const modalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;1,400&family=Syne:wght@300;400;500;600&display=swap');

  .cm-overlay{position:fixed;inset:0;background:rgba(10,15,30,0.5);backdrop-filter:blur(6px);z-index:1000;display:flex;align-items:center;justify-content:center;padding:1.5rem}
  .cm-box{font-family:'Syne',sans-serif;background:#ffffff;border:1px solid rgba(10,15,30,0.08);border-radius:12px;width:100%;max-width:580px;max-height:90vh;overflow-y:auto;position:relative;scrollbar-width:none}
  .cm-box::-webkit-scrollbar{display:none}

  /* Top accent */
  .cm-accent{height:3px;background:linear-gradient(90deg,#00897B,#0A0F1E);border-radius:12px 12px 0 0}

  .cm-inner{padding:2.5rem 2.5rem 2rem}

  .cm-close{position:absolute;top:1.25rem;right:1.25rem;width:30px;height:30px;background:rgba(10,15,30,0.05);border:1px solid rgba(10,15,30,0.1);border-radius:6px;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:background 0.2s;z-index:2}
  .cm-close:hover{background:rgba(10,15,30,0.1)}
  .cm-close svg{width:14px;height:14px;stroke:rgba(10,15,30,0.5)}

  .cm-eyebrow{font-size:10px;letter-spacing:2.5px;text-transform:uppercase;color:#00897B;margin-bottom:0.5rem}
  .cm-title{font-family:'Playfair Display',serif;font-size:28px;color:#0A0F1E;font-weight:400;line-height:1.2;margin-bottom:0.5rem}
  .cm-sub{font-size:12px;color:#6C7589;line-height:1.6;margin-bottom:2rem;font-weight:300}

  .cm-form{display:flex;flex-direction:column;gap:1.25rem}
  .cm-field{display:flex;flex-direction:column;gap:0.4rem}
  .cm-row{display:grid;grid-template-columns:1fr 1fr;gap:1rem}
  .cm-row-3{display:grid;grid-template-columns:1fr 1fr 0.6fr;gap:0.75rem}

  .cm-label{font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:#6C7589;font-weight:500}

  .cm-input, .cm-select, .cm-textarea{
    width:100%;
    background:#F4F5F7;
    border:1px solid rgba(10,15,30,0.1);
    border-radius:6px;
    padding:0.75rem 1rem;
    font-size:13px;
    font-family:'Syne',sans-serif;
    color:#0A0F1E;
    outline:none;
    transition:border-color 0.2s, background 0.2s;
    -webkit-appearance:none;
  }
  .cm-input::placeholder, .cm-textarea::placeholder{color:rgba(10,15,30,0.3)}
  .cm-input:focus, .cm-select:focus, .cm-textarea:focus{
    border-color:#00897B;
    background:#ffffff;
  }
  .cm-select option{background:#ffffff;color:#0A0F1E}
  .cm-textarea{resize:none;min-height:110px;line-height:1.6}

  .cm-divider{height:1px;background:rgba(10,15,30,0.07);margin:0.25rem 0}

  .cm-submit{
    width:100%;
    background:#0A0F1E;
    color:white;
    font-family:'Syne',sans-serif;
    font-size:12px;
    font-weight:500;
    letter-spacing:0.75px;
    text-transform:uppercase;
    padding:14px 24px;
    border-radius:6px;
    border:none;
    cursor:pointer;
    transition:background 0.2s;
    margin-top:0.25rem;
  }
  .cm-submit:hover{background:#00897B}

  @media (max-width: 520px) {
    .cm-inner{padding:2rem 1.5rem 1.75rem}
    .cm-row{grid-template-columns:1fr}
    .cm-row-3{grid-template-columns:1fr 1fr}
    .cm-title{font-size:24px}
  }
`;

const ContactModal = ({ open, handleClose, formData, handleChange, handleSubmit }) => {
  if (!open) return null;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: modalStyles }} />
      <AnimatePresence>
        {open && (
          <motion.div
            className="cm-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
          >
            <motion.div
              className="cm-box"
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.96 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <div className="cm-accent"></div>

              <div className="cm-inner">
                <button className="cm-close" onClick={handleClose} aria-label="Close">
                  <svg fill="none" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <div className="cm-eyebrow">Contact Us</div>
                <div className="cm-title">Get In Touch</div>
                <div className="cm-sub">Fill out the form below and our team will respond within one business day.</div>

                <form onSubmit={handleSubmit} className="cm-form">

                  <div className="cm-row">
                    <div className="cm-field">
                      <label className="cm-label">Provider Name</label>
                      <input className="cm-input" type="text" name="name" value={formData.name}
                        onChange={handleChange} required placeholder="Dr. John Smith" />
                    </div>
                    <div className="cm-field">
                      <label className="cm-label">Facility Name</label>
                      <input className="cm-input" type="text" name="facility" value={formData.facility}
                        onChange={handleChange} required placeholder="Clinic or practice name" />
                    </div>
                  </div>

                  <div className="cm-row-3">
                    <div className="cm-field">
                      <label className="cm-label">City</label>
                      <input className="cm-input" type="text" name="city" value={formData.city}
                        onChange={handleChange} required placeholder="City" />
                    </div>
                    <div className="cm-field">
                      <label className="cm-label">State</label>
                      <select className="cm-select" name="state" value={formData.state}
                        onChange={handleChange} required>
                        <option value="">Select</option>
                        {states.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="cm-field">
                      <label className="cm-label">Zip</label>
                      <input className="cm-input" type="text" name="zip" value={formData.zip}
                        onChange={handleChange} required placeholder="00000" />
                    </div>
                  </div>

                  <div className="cm-row">
                    <div className="cm-field">
                      <label className="cm-label">Phone Number</label>
                      <input className="cm-input" type="tel" name="phone" value={formData.phone}
                        onChange={handleChange} required placeholder="(123) 456-7890" />
                    </div>
                    <div className="cm-field">
                      <label className="cm-label">Email Address</label>
                      <input className="cm-input" type="email" name="email" value={formData.email}
                        onChange={handleChange} required placeholder="you@example.com" />
                    </div>
                  </div>

                  <div className="cm-field">
                    <label className="cm-label">Your Question</label>
                    <textarea className="cm-textarea" name="question" value={formData.question}
                      onChange={handleChange} required placeholder="Tell us how we can help…" />
                  </div>

                  <div className="cm-divider"></div>

                  <button type="submit" className="cm-submit">Send Message</button>

                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ContactModal;